import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Lock, Loader2, Clock, CheckCircle } from 'lucide-react';

import { BACKEND_URL } from '../config/api';

/**
 * Button employers see on a tradesperson profile. Three modes:
 *
 *  - `messages_only`                → "Phone unavailable" (locked)
 *  - `approved_employers_can_contact` → "Reveal phone number" (direct reveal)
 *  - `request_first`                 → "Request phone access" → waits
 *                                      for worker approval → "Reveal
 *                                      phone number" once approved
 *
 * The server enforces every rule. Frontend only picks the right CTA.
 */
export default function RevealPhoneButton({ userId }) {
  const [state, setState] = useState({
    status: 'idle',
    phone: null,
    mode: null,
    message: null,
    needsRequest: false,
  });
  const [accessStatus, setAccessStatus] = useState('unknown');

  const refreshAccessStatus = useCallback(async () => {
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/tradespeople/${userId}/phone-access-status`,
        { withCredentials: true }
      );
      setAccessStatus(r.data?.status || 'none');
    } catch (e) {
      setAccessStatus('unknown');
    }
  }, [userId]);

  useEffect(() => { refreshAccessStatus(); }, [refreshAccessStatus]);

  const reveal = async () => {
    if (state.status === 'loading') return;
    setState((s) => ({ ...s, status: 'loading', message: null }));
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/tradespeople/${userId}/reveal-phone`,
        {},
        { withCredentials: true }
      );
      if (r.data?.reveal_allowed && r.data?.phone) {
        setState({ status: 'revealed', phone: r.data.phone, mode: r.data.mode, message: null, needsRequest: false });
      } else if (r.data?.reveal_allowed && !r.data?.phone) {
        setState({ status: 'empty', phone: null, mode: r.data.mode, message: r.data.message || 'No phone number is stored.', needsRequest: false });
      } else {
        setState({
          status: 'denied',
          phone: null,
          mode: r.data?.mode,
          message: r.data?.message || 'Phone number is not available.',
          needsRequest: Boolean(r.data?.needs_request),
        });
      }
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === 429) {
        toast.error('Too many reveals. Please wait before trying again.');
        setState({ status: 'rate_limited', phone: null, mode: null, message: detail?.message || 'Rate limit exceeded.', needsRequest: false });
      } else if (code === 503) {
        toast.error('Phone reveal temporarily unavailable.');
        setState({ status: 'unavailable', phone: null, mode: null, message: detail?.message || 'Try again shortly.', needsRequest: false });
      } else if (code === 403 || code === 401) {
        toast.error('You are not authorised to view this number.');
        setState({ status: 'denied', phone: null, mode: null, message: null, needsRequest: false });
      } else {
        toast.error('Could not reveal phone number.');
        setState({ status: 'idle', phone: null, mode: null, message: null, needsRequest: false });
      }
    }
  };

  const sendRequest = async () => {
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/tradespeople/${userId}/request-phone-access`,
        {},
        { withCredentials: true }
      );
      setAccessStatus(r.data?.status || 'pending');
      toast.success(r.data?.status === 'approved'
        ? 'Already approved — you can now reveal the number.'
        : 'Request sent. The tradesperson will review it.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || 'Could not send request.');
    }
  };

  if (state.status === 'revealed') {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="phone-reveal-revealed">
        <p className="text-xs uppercase tracking-wide text-emerald-700">Phone number</p>
        <p className="text-xl font-bold text-emerald-900">{state.phone}</p>
      </div>
    );
  }

  // request_first + no approval → show "Request Phone Access"
  const showRequestFlow = state.needsRequest || accessStatus === 'pending' || accessStatus === 'declined' || accessStatus === 'revoked';
  if (showRequestFlow) {
    const canSend = accessStatus === 'none' || accessStatus === 'declined' || accessStatus === 'revoked' || accessStatus === 'unknown';
    const label =
      accessStatus === 'pending' ? 'Request pending — awaiting approval' :
      accessStatus === 'declined' ? 'Request declined' :
      accessStatus === 'revoked' ? 'Approval revoked' :
      'Request phone access';
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={sendRequest}
          disabled={!canSend}
          className="btn-secondary flex items-center gap-2 whitespace-nowrap"
          data-testid="phone-request-btn"
        >
          <Clock className="h-4 w-4" />
          {label}
        </button>
        {state.message && (
          <p className="text-xs text-slate-500" data-testid="phone-reveal-message">
            {state.message}
          </p>
        )}
      </div>
    );
  }

  if (accessStatus === 'approved' && state.status === 'idle') {
    // Nudge the employer to click reveal — they're approved.
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={reveal}
          className="btn-primary flex items-center gap-2 whitespace-nowrap"
          data-testid="phone-reveal-btn"
        >
          <CheckCircle className="h-4 w-4" />
          Reveal phone number (approved)
        </button>
      </div>
    );
  }

  const label =
    state.status === 'loading' ? 'Checking…' :
    state.status === 'rate_limited' ? 'Try again later' :
    state.status === 'unavailable' ? 'Unavailable — try again' :
    state.status === 'denied' || state.status === 'empty' ? 'Phone unavailable' :
    'Reveal phone number';

  const Icon =
    state.status === 'loading' ? Loader2 :
    (state.status === 'denied' || state.status === 'empty' || state.status === 'rate_limited' || state.status === 'unavailable') ? Lock :
    Phone;

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={reveal}
        disabled={state.status === 'loading' || state.status === 'denied' || state.status === 'empty'}
        className="btn-secondary flex items-center gap-2 whitespace-nowrap"
        data-testid="phone-reveal-btn"
      >
        <Icon className={`h-4 w-4 ${state.status === 'loading' ? 'animate-spin' : ''}`} />
        {label}
      </button>
      {state.message && (
        <p className="text-xs text-slate-500" data-testid="phone-reveal-message">
          {state.message}
        </p>
      )}
    </div>
  );
}
